import { HTTP_URL } from "../config/backend";
import { useCallback, useRef } from "react";

const API = HTTP_URL;

// ── Кеш audio blob URL ────────────────────────────────────────────────────────
// Хранит уже загруженные фразы чтобы не дёргать сервер повторно
const _cache = new Map(); // "text|game" → blobUrl

// ── useTTS ────────────────────────────────────────────────────────────────────
// Хук для озвучки текста через Yandex SpeechKit (бэкенд-прокси).
// При ошибке сети автоматически fallback на Web Speech API.
//
// Использование:
//   const { speak, stop } = useTTS();
//   speak("Вопрос: кто первым полетел в космос?");
//
export default function useTTS() {
  const audioRef   = useRef(null); // текущий <Audio> объект
  const utterRef   = useRef(null); // текущий SpeechSynthesisUtterance
  const loadingRef = useRef(false);

  // ── Остановить текущее воспроизведение ──────────────────────────────────
  const stop = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = "";
      audioRef.current = null;
    }
    if (utterRef.current) {
      window.speechSynthesis?.cancel();
      utterRef.current = null;
    }
    loadingRef.current = false;
  }, []);

  // ── Fallback: Web Speech API ─────────────────────────────────────────────
  const speakWeb = useCallback((text) => {
    if (!window.speechSynthesis) return;
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(text);
    u.lang  = "ru-RU";
    u.rate  = 0.92;
    u.pitch = 1.05;
    // Выбираем русский голос если есть
    const voices = window.speechSynthesis.getVoices();
    const ruVoice = voices.find(v => v.lang.startsWith("ru"));
    if (ruVoice) u.voice = ruVoice;
    utterRef.current = u;
    window.speechSynthesis.speak(u);
  }, []);

  // ── Основная функция озвучки ─────────────────────────────────────────────
  const speak = useCallback(async (text, game = "") => {
    if (!text?.trim()) return;
    stop();

    const trimmed = text.trim();

    const cacheKey = game ? `${trimmed}|${game}` : trimmed;

    // Из кеша — сразу играем
    if (_cache.has(cacheKey)) {
      const audio = new Audio(_cache.get(cacheKey));
      audioRef.current = audio;
      audio.play().catch(() => speakWeb(trimmed));
      return;
    }

    // Запрашиваем с бэка
    loadingRef.current = true;
    try {
      const url = `${API}/api/tts?text=${encodeURIComponent(trimmed)}${game ? `&game=${game}` : ""}`;
      const resp = await fetch(url);

      // 403 = TTS отключён для этой игры — молчим, без fallback
      if (resp.status === 403) {
        console.info(`[TTS] disabled for game ${game}`);
        return;
      }
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);

      const blob    = await resp.blob();
      const blobUrl = URL.createObjectURL(blob);

      // Кешируем (не больше 200 записей)
      if (_cache.size >= 200) {
        const firstKey = _cache.keys().next().value;
        URL.revokeObjectURL(_cache.get(firstKey));
        _cache.delete(firstKey);
      }
      _cache.set(cacheKey, blobUrl);

      if (!loadingRef.current) return; // stop() был вызван пока грузили

      const audio = new Audio(blobUrl);
      audioRef.current = audio;
      audio.play().catch(() => speakWeb(trimmed));
    } catch (e) {
      // Яндекс недоступен — fallback на браузерный TTS
      console.warn("[TTS] Yandex failed, using Web Speech:", e.message);
      speakWeb(trimmed);
    } finally {
      loadingRef.current = false;
    }
  }, [stop, speakWeb]);

  return { speak, stop };
}